import React, { useRef, useEffect, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  getDualLineChartOptions,
  formatTagMonitorChartData,
} from "./chartUtils";
import { formatNumber } from "../../utils";
import ChartNoData from "../ChartNoData";
import "./TagMonitorChart.scss";

// Percentage with one decimal in the same number format as the event counts
// next to it, so "1.250 events" goes with "12,5%" and "1,250" with "12.5%"
const formatRate = (part, total) =>
  ((part / total) * 100).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

/**
 * Tag Monitor Server-side Dual-line Chart
 * Displays successful events vs error events with purple/green color scheme
 */
const TagMonitorChart = ({
  serverData = [],
  title = "Server-side Tag Monitoring",
  height = 350,
  loading = false,
  onDataPointClick = null,
  showLegend = true,
}) => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  // Format data for Chart.js
  const chartData = useMemo(() => {
    return formatTagMonitorChartData(serverData);
  }, [serverData]);

  // Chart options with custom styling
  const chartOptions = useMemo(() => {
    const baseOptions = getDualLineChartOptions(title);

    return {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        legend: {
          ...baseOptions.plugins.legend,
          display: showLegend,
        },
        tooltip: {
          ...baseOptions.plugins.tooltip,
          callbacks: {
            title: (context) => {
              return `Time: ${context[0].label}`;
            },
            label: (context) => {
              const value = context.parsed.y;
              return `${
                context.dataset.label
              }: ${formatNumber(value)} events`;
            },
            afterLabel: (context) => {
              const successData = chartData.datasets.find(
                (d) => d.label === "Successful events",
              );
              const errorData = chartData.datasets.find(
                (d) => d.label === "Error events",
              );

              //================================================================
              // % Rate calculation logic:
              //================================================================
              if (successData && errorData) {
                const successValue = successData.data[context.dataIndex] || 0;
                const errorValue = errorData.data[context.dataIndex] || 0;
                const total = successValue + errorValue;

                if (total > 0) {
                  const successRate = formatRate(successValue, total);
                  const errorRate = formatRate(errorValue, total);

                  // Only show the rate for the line being hovered
                  if (context.dataset.label === "Successful events") {
                    return `Success rate: ${successRate}%`;
                  } else if (context.dataset.label === "Error events") {
                    return `Error rate: ${errorRate}%`;
                  }
                }
              }
              //================================================================
              // % Rate calculation logic ends
              //================================================================
              return "";
            },
          },
        },
      },
      onClick: (event, elements) => {
        if (elements.length > 0 && onDataPointClick) {
          const element = elements[0];
          const datasetIndex = element.datasetIndex;
          const dataIndex = element.index;
          const dataset = chartData.datasets[datasetIndex];
          const label = chartData.labels[dataIndex];
          const value = dataset.data[dataIndex];

          onDataPointClick({
            eventType: dataset.label,
            timestamp: label,
            eventCount: value,
            datasetIndex,
            dataIndex,
          });
        }
      },
      onHover: (event, elements) => {
        const chart = chartRef.current;
        if (chart && chart.canvas) {
          chart.canvas.style.cursor =
            elements.length > 0 ? "pointer" : "default";
        }
      },
    };
  }, [title, chartData, onDataPointClick, showLegend]);

  // Handle responsive sizing
  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current && containerRef.current) {
        chartRef.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="tagmonitor-chart-container" ref={containerRef}>
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading server data...</p>
        </div>
      </div>
    );
  }

  // Always show the chart; show a no data message when there is no data
  return (
    <div className="tagmonitor-chart-container" ref={containerRef}>
      <ChartNoData show={!serverData?.length} dataName="Tag Monitor">
        <Line ref={chartRef} data={chartData} options={chartOptions} />
      </ChartNoData>
    </div>
  );
};

export default TagMonitorChart;
